import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import {
  AppRoute,
  AuthHandler,
  ConfigSettingsService,
  LoginError,
  LoginWithPasswordError,
  MessageBase,
  UserActivationMissingError,
} from '@ebeihilfe/core';
import { OverlayService } from '@ebeihilfe/web-logic';
import { NoteComponent } from '@ebeihilfe/web-shared/note';
import { PasswordInputComponent } from '@ebeihilfe/web-shared/password-input';
import { UsernameInputComponent } from '@ebeihilfe/web-shared/username-input/username-input.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'portal-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    UsernameInputComponent,
    PasswordInputComponent,
    NoteComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent extends AuthHandler implements OnInit {
  public isPasswordGrantAvailable = signal(false);
  public loginForm = signal<FormGroup>(
    new FormGroup({
      password: new FormControl<string>(''),
      personalNumber: new FormControl<string>(''),
    }),
  );

  protected readonly message = inject(MessageBase);
  protected readonly router = inject(Router);
  protected readonly configService = inject(ConfigSettingsService);
  protected readonly spinnerService = inject(OverlayService);

  public ngOnInit(): void {
    this.spinnerService.hide();
    this.isPasswordGrantAvailable.set(this.configService.appSettings.supportedOAuthGrants.has('password'));
    super.ngOnInit();
  }

  public async submitLogin(): Promise<void> {
    if (this.loginForm()?.invalid ?? false) {
      return;
    }

    const username: string = this.loginForm()?.get('personalNumber')?.value.trim();
    const password: string = this.loginForm()?.get('password')?.value.trim();

    const wasLoginSuccessful = await this.loginWithPassword(username, password);
    if (wasLoginSuccessful) {
      await this.router.navigate([AppRoute.DashboardPage]);
      this.loginForm().reset();
    }
  }

  protected async handleCodeLoginError(error: LoginError): Promise<void> {
    this.message.showError({ message: error.message });
    await this.router.navigate([AppRoute.Login]);
  }

  protected handleLoginWithPasswordError(error: LoginWithPasswordError, { username, password }): void {
    const classifiedError = this.classifyError(error);

    if (classifiedError instanceof UserActivationMissingError) {
      this.loginForm().reset();
      this.router.navigate([AppRoute.UnfinishedRegistration], {
        state: {
          username,
          password,
        },
      });
      return;
    }
  }

  protected onCodeLogin(): void {
    this.router.navigate([AppRoute.DashboardPage]);
  }

  protected onLoadingEnd(): Promise<void> {
    this.spinnerService.hide();
    return Promise.resolve();
  }

  protected onLoadingStart(): Promise<void> {
    this.spinnerService.show();
    return Promise.resolve();
  }
}
